import React, { useEffect, useState } from "react";
import { Pencil, Plus, Share2 } from "lucide-react";
import Sidebar from "../../../component/includes/sidebar";
import { 
  FooterContent6, 
  footercontent6Service, 
  UpdateFooterContent6Data 
} from "../../../services/footer/sosialmedia/inex";

const FooterContent6Management: React.FC = () => {
  const [footerContent, setFooterContent] = useState<FooterContent6[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFooterContent, setEditingFooterContent] = useState<FooterContent6 | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchFooterContent = async () => {
    try {
      setLoading(true);
      const response = await footercontent6Service.getFooterContent6();
      setFooterContent(Array.isArray(response) ? response : []);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to fetch footer content");
      setFooterContent([]);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFooterContent();
  }, []);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 3000);
  };

  const resetForm = () => {
    setEditingFooterContent(null);
    setIsModalOpen(false);
  };

  const filteredFooterContent = footerContent.filter((content) =>
    content.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUpdateFooterContent = async () => {
    if (!editingFooterContent) return;

    if (!editingFooterContent.title.trim()) {
      showError("Title cannot be empty");
      return;
    }

    try {
      const updatedData: UpdateFooterContent6Data = {
        id: editingFooterContent.id,
        title: editingFooterContent.title,
        link_ig: editingFooterContent.link_ig,
        link_email: editingFooterContent.link_email,
      };

      await footercontent6Service.updateFooterContent6(updatedData);
      await fetchFooterContent();
      showSuccess("Footer content updated successfully");
      resetForm();
    } catch (err) {
      showError("Failed to update footer content");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center">
                  <Share2 size={32} className="text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Footer Content 6 Management</h1>
                  <p className="text-gray-500 text-sm mt-1">Manage social media and email links for footer</p>
                </div>
              </div>

              {/* Tombol Add New (siap jika service mendukung create) */}
              <button
                onClick={() => {/* logic create jika ada */}}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all active:scale-95"
              >
                <Plus className="w-5 h-5" />
                Add New Link
              </button>
              
            </div>

            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl py-4 pl-12 pr-6 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-all"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400">🔍</div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">TITLE</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">INSTAGRAM LINK</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">EMAIL LINK</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-32">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredFooterContent.map((content, index) => (
                    <tr key={content.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-8 py-6">
                        <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                          {index + 1}
                        </div>
                      </td>
                      <td className="px-8 py-6 font-medium text-gray-800">
                        {content.title}
                      </td>
                      <td className="px-8 py-6 text-gray-600 break-all">
                        <a 
                          href={content.link_ig} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-pink-600 hover:underline"
                        >
                          {content.link_ig || "-"}
                        </a>
                      </td>
                      <td className="px-8 py-6 text-gray-600 break-all">
                        <a 
                          href={content.link_email} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-blue-600 hover:underline"
                        >
                          {content.link_email || "-"}
                        </a>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex justify-end">
                          <button
                            onClick={() => {
                              setEditingFooterContent(content);
                              setIsModalOpen(true);
                            }}
                            className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {filteredFooterContent.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-20 text-center text-gray-400">
                        No social media content found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && editingFooterContent && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-semibold mb-6">Edit Social Media Links</h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={editingFooterContent.title}
                  onChange={(e) => setEditingFooterContent({ ...editingFooterContent, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-pink-500"
                  placeholder="Contoh: Follow Our Social Media"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Link</label>
                  <input
                    type="text"
                    value={editingFooterContent.link_ig || ""}
                    onChange={(e) => setEditingFooterContent({ ...editingFooterContent, link_ig: e.target.value })}
                    className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-pink-500"
                    placeholder="https://www.instagram.com/..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Link</label>
                  <input
                    type="text"
                    value={editingFooterContent.link_email || ""}
                    onChange={(e) => setEditingFooterContent({ ...editingFooterContent, link_email: e.target.value })}
                    className="w-full border border-gray-300 rounded-2xl px-6 py-4 focus:outline-none focus:border-blue-500"
                    placeholder="mailto:info@wkjtégal.com"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4 mt-10">
              <button
                onClick={resetForm}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateFooterContent}
                className="flex-1 py-4 bg-pink-600 hover:bg-pink-700 text-white rounded-2xl font-medium transition-all"
              >
                Update Links
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FooterContent6Management;